import {
    getObjects,
    createObjects,
    updateObjects,
    deleteObjects,
} from './../api/webApplicationApi';
import { v4 as uuidv4 } from 'uuid';
import { Site } from './site';
import { Role } from './role';
import { Policy } from './policy';

const objectType = 'siteGroup';

export class SiteGroup {
    private _saveMethod = createObjects;
    private _object: omSiteGroup;
    private _lastError: Error;

    public constructor(siteGroupID: string) {
        this._object = {
            siteGroupID: siteGroupID,
            accountID: undefined,
            name: undefined,
            parentObjectType: undefined,
            parentObjectID: undefined,
            sites: [],
            roles: [],
            policies: [],
        };
    }

    public static createNew(): SiteGroup {
        return new SiteGroup(uuidv4());
    }

    public static createFromObject(siteGroup: omSiteGroup): SiteGroup {
        const obj = new SiteGroup(siteGroup.siteGroupID);
        obj._object = siteGroup;
        obj._saveMethod = updateObjects;
        return obj;
    }

    public async load(): Promise<boolean> {
        const getObject: GetRequest = {
            objectType: objectType,
            keys: [this._object.siteGroupID],
        };
        this._lastError = undefined;
        try {
            const response = await getObjects([getObject]);
            if (
                response &&
                response.length > 0 &&
                response[0].objects &&
                response[0].objects.length == 1
            ) {
                this._saveMethod = updateObjects;
                this._object = response[0].objects[0];
                return true;
            } else {
                this._lastError = new Error('site group not found');
                return false;
            }
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async save(): Promise<boolean> {
        const saveObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;

        if (this._object.name == undefined) {
            this._lastError = new Error('Name is undefined');
            return false;
        }
        if (this._object.accountID == undefined) {
            this._lastError = new Error('Account ID is undefined');
            return false;
        }
        if (this._object.parentObjectType == undefined) {
            this._lastError = new Error('Parent Group Type is undefined');
            return false;
        }
        if (this._object.parentObjectID == undefined) {
            this._lastError = new Error('Parent Group ID is undefined');
            return false;
        }
        try {
            const response = await this._saveMethod([saveObject]);
            if (response) {
                this._saveMethod = updateObjects;
            } else {
                this._lastError = new Error('unknow server error');
            }
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async delete(): Promise<boolean> {
        const deleteObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;
        try {
            const response = await deleteObjects([deleteObject]);
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public get lastError(): Error {
        return this._lastError;
    }

    public get siteGroupID(): string {
        return this._object.siteGroupID;
    }

    public get accountID(): string {
        return this._object.accountID;
    }

    public set accountID(accountID: string) {
        this._object.accountID = accountID;
    }

    public get name(): string {
        return this._object.name;
    }

    public set name(name: string) {
        this._object.name = name;
    }

    public get parentObjectType(): string {
        return this._object.parentObjectType;
    }

    public set parentObjectType(parentObjectType: string) {
        this._object.parentObjectType = parentObjectType;
    }

    public get parentObjectID(): string {
        return this._object.parentObjectID;
    }

    public set parentObjectID(parentObjectID: string) {
        this._object.parentObjectID = parentObjectID;
    }

    public get sites(): string[] {
        return this._object.sites;
    }

    public addSite(site: Site): void {
        if (this._object.sites.includes(site.siteID)) return;
        this._object.sites.push(site.siteID);
    }

    public deleteSite(site: Site): void {
        const index = this._object.sites.indexOf(site.siteID);
        if (index > -1) {
            this._object.sites.splice(index, 1);
        }
    }

    public get roles(): string[] {
        return this._object.roles;
    }

    public addRole(role: Role): void {
        if (this._object.roles.includes(role.roleID)) return;
        this._object.roles.push(role.roleID);
    }

    public deleteRole(role: Role): void {
        const index = this._object.roles.indexOf(role.roleID);
        if (index > -1) {
            this._object.roles.splice(index, 1);
        }
    }

    public get policies(): string[] {
        return this._object.policies;
    }

    public addPolicy(policy: Policy): void {
        if (this._object.policies.includes(policy.policyID)) return;
        this._object.policies.push(policy.policyID);
    }

    public deletePolicy(policy: Policy): void {
        const index = this._object.policies.indexOf(policy.policyID);
        if (index > -1) {
            this._object.policies.splice(index, 1);
        }
    }
}
